import {
  useApproveTeamKPIMutation,
  useCreateOwnKPIMutation,
  useDraftOwnKPIMutation,
  useDraftTeamKPIMutation,
  useGetOwnKPIDetailsQuery,
  useGetTeamKPIDetailsQuery,
  useReturnTeamKPIMutation,
} from "@/app/features/employee/performance/performance.api";
import CustomDrawer from "@/components/CustomDrawer";
import MultiFileUploader from "@/components/MultiFileUploader";
import WithLoader from "@/components/WithLoader";
import useAuth from "@/hooks/useAuth";
import { XIcon } from "@/icons";
import { cn } from "@/lib/cn";
import { getErrorMessage } from "@/lib/error";
import { toast } from "@/lib/toast";
import { getFileNameFromUrl } from "@/utils";
import { Button, Flex, Form, Input, InputNumber } from "antd";
import TextArea from "antd/es/input/TextArea";
import { useEffect } from "react";

// ──────── Types ────────
interface ManageOwnKPIProps {
  open: boolean;
  onClose: () => void;
  planName?: string;
  mappingId?: string;
  planId?: string;
  employeeId?: string;
  mode?: "add" | "view" | "edit";
  source?: "OWN" | "TEAM";
}

type FormType = {
  kpis: {
    kpi_id?: string;
    kpi_name: string;
    details?: string;
    weightage: number;
  }[];
  attachments?: string[]; // Array of URLs
  manager_comment?: string;
};

// ──────── Component ────────
export default function ManageOwnKPI({
  open,
  onClose,
  planName,
  mappingId,
  planId,
  employeeId,
  mode = "add",
  source = "OWN",
}: ManageOwnKPIProps) {
  const { userId, organizationId } = useAuth();
  const [form] = Form.useForm<FormType>();

  // Mutations
  const [draftOwnKPI, { isLoading: isDraftingOwn }] = useDraftOwnKPIMutation();
  const [createOwnKPI, { isLoading: isCreatingOwn }] =
    useCreateOwnKPIMutation();
  const [draftTeamKPI, { isLoading: isDraftingTeam }] =
    useDraftTeamKPIMutation();
  const [approveTeamKPI, { isLoading: isApprovingTeam }] =
    useApproveTeamKPIMutation();
  const [returnTeamKPI, { isLoading: isReturningTeam }] =
    useReturnTeamKPIMutation();

  const isTeam = source === "TEAM";

  // Details queries
  const { data: ownDetailRes, isFetching: isOwnDetailFetching } =
    useGetOwnKPIDetailsQuery(mappingId || "", {
      skip: !mappingId || isTeam || mode === "add" || !open,
    });

  const { data: teamDetailRes, isFetching: isTeamDetailFetching } =
    useGetTeamKPIDetailsQuery(mappingId || "", {
      skip: !mappingId || !isTeam || mode === "add" || !open,
    });

  const detailRes = isTeam ? teamDetailRes : ownDetailRes;
  const isDetailFetching = isTeam ? isTeamDetailFetching : isOwnDetailFetching;

  const isDrafting = isTeam ? isDraftingTeam : isDraftingOwn;
  const isCreating = isTeam ? isApprovingTeam : isCreatingOwn;

  const managerMappings = detailRes?.data?.managerMappings || [];
  // const activeComments = managerMappings.filter((m: any) => m.kpiComment);

  useEffect(() => {
    if (detailRes?.data) {
      const targetData = detailRes.data;
      const employeeKpis =
        targetData.employeeKpis || targetData.managerMappings || [];

      const kpis = employeeKpis.map((k: any) => ({
        kpi_id: k.id || k.kpi_id,
        kpi_name: k.kpiName || k.kpi_name,
        details: k.details,
        weightage: Number(k.weightage),
      }));

      form.setFieldsValue({
        kpis: kpis?.length
          ? kpis
          : [{ kpi_name: "", weightage: undefined, details: "" }],
        attachments:
          targetData.attachments?.map((file: any) => file.fileUrl) || [],
        manager_comment: targetData.manager_comment,
      });
    }
  }, [detailRes, form, isTeam, open]);

  const handleSubmit = (type: "draft" | "submit" | "return") => {
    if (type === "return") {
      const managerComment = form.getFieldValue("manager_comment");
      const returnPayload = {
        mapping_id: mappingId || "",
        manager_id: userId || "",
        manager_comment: managerComment || "",
      };

      console.log("Formatted submitted value (Return):", returnPayload);

      returnTeamKPI(returnPayload)
        .unwrap()
        .then((res) => {
          if (res?.success === false) {
            toast("error", getErrorMessage(res, "Failed to return KPI"));
            return;
          }
          toast("success", "KPI returned successfully");
          handleDrawerClose();
        })
        .catch((error) => {
          toast("error", getErrorMessage(error, "Failed to return KPI"));
        });
      return;
    }

    form
      .validateFields()
      .then(async (values) => {
        // Calculate total weightage
        const totalWeightage = (values.kpis || []).reduce(
          (acc, curr) => acc + (Number(curr.weightage) || 0),
          0,
        );

        if (totalWeightage !== 100) {
          toast("error", "Total weightage must be exactly 100%");
          return;
        }

        const commonPayload = {
          mapping_id: mappingId || "",
          plan_id: planId || "",
          organization_id: organizationId || "",
          employee_id: isTeam ? employeeId || "" : userId || "",
          kpis: values.kpis.map((k) => ({
            ...k,
            weightage: Number(k.weightage),
          })),
          attachments:
            values.attachments?.map((url) => ({
              file_name: getFileNameFromUrl(url),
              file_url: url,
            })) || [],
        };

        const payload = isTeam
          ? {
              ...commonPayload,
              manager_id: userId || "",
              manager_comment: values.manager_comment,
            }
          : commonPayload;

        try {
          let res;
          if (type === "draft") {
            res = isTeam
              ? await draftTeamKPI(payload as any).unwrap()
              : await draftOwnKPI(payload as any).unwrap();
          } else {
            res = isTeam
              ? await approveTeamKPI(payload as any).unwrap()
              : await createOwnKPI(payload as any).unwrap();
          }

          if (res?.success === false) {
            toast("error", getErrorMessage(res, `Failed to ${type} KPI`));
            return;
          }

          toast(
            "success",
            `KPI ${type === "draft" ? "drafted" : "submitted"} successfully`,
          );
          handleDrawerClose();
        } catch (error: any) {
          toast("error", getErrorMessage(error, `Failed to ${type} KPI`));
        }
      })
      .catch((err) => {
        console.error("Validation Failed:", err);
      });
  };

  const handleDrawerClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={handleDrawerClose}
      title={planName || "KPI Setup"}
    >
      <WithLoader
        isLoading={isDetailFetching}
        className={cn(
          "rounded-lg",
          isDetailFetching && "h-[calc(100vh_-_105px)]",
        )}
      >
        <Form
          form={form}
          layout="vertical"
          className="custom-form"
          size="large"
          initialValues={{
            kpis: [
              { kpi_name: "", weightage: undefined, details: "" },
              { kpi_name: "", weightage: undefined, details: "" },
            ],
          }}
        >
          <>
            {/* KPI List Container */}
            <div className="border border-theme-gray-700 rounded-xl p-6">
              <h4 className="text-white text-base font-semibold mb-6">
                KPI List <span className="text-red-500">*</span>
              </h4>

              <Form.List
                name="kpis"
                rules={[
                  {
                    validator: async (_, kpis) => {
                      if (!kpis || kpis.length === 0) {
                        return Promise.reject(
                          new Error("At least one KPI is required"),
                        );
                      }
                    },
                  },
                ]}
              >
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => {
                      const { key, ...restField } = field;
                      return (
                        <div key={key} className="relative">
                          {/* Separator between KPI items */}
                          {index > 0 && (
                            <div className="border-t border-dashed border-theme-gray-600 my-6" />
                          )}

                          {/* KPI Item Header */}
                          <Flex
                            justify="space-between"
                            align="center"
                            className="mb-4"
                          >
                            <h5 className="text-white text-sm font-medium ">
                              KPI -{index + 1}
                            </h5>
                            {fields.length > 1 && (
                              <Button
                                danger
                                type="text"
                                size="small"
                                icon={<XIcon size={18} />}
                                onClick={() => remove(field.name)}
                                classNames={{
                                  icon: "flex items-center justify-center hover:bg-red-500/10 p-1",
                                }}
                              />
                            )}
                          </Flex>

                          {/* KPI Name + Weightage Row */}
                          <div className="grid grid-cols-2 gap-6">
                            <Form.Item
                              {...restField}
                              label="Name"
                              name={[field.name, "kpi_name"]}
                              rules={[
                                {
                                  required: true,
                                  message: "KPI name is required",
                                },
                              ]}
                              className="mb-4"
                            >
                              <Input placeholder="Enter KPI name" />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              label="Weightage(%)"
                              name={[field.name, "weightage"]}
                              rules={[
                                {
                                  required: true,
                                  message: "Weightage is required",
                                },
                                {
                                  type: "number",
                                  min: 1,
                                  message: "Must be at least 1",
                                },
                                {
                                  type: "number",
                                  max: 100,
                                  message: "Must not exceed 100",
                                },
                              ]}
                              className="mb-4"
                            >
                              <InputNumber
                                controls={false}
                                placeholder="Enter weightage"
                                className="w-full"
                              />
                            </Form.Item>
                          </div>

                          {/* Details */}
                          <Form.Item
                            {...restField}
                            label="Details"
                            name={[field.name, "details"]}
                            className="mb-0"
                          >
                            <TextArea rows={4} placeholder="Enter details" />
                          </Form.Item>
                        </div>
                      );
                    })}

                    {/* Add New KPI */}
                    {!isTeam && (
                      <button
                        type="button"
                        onClick={() =>
                          add({
                            kpi_name: "",
                            weightage: undefined,
                            details: "",
                          })
                        }
                        className="w-full flex justify-center text-theme-primary relative mt-6"
                      >
                        <div className="absolute left-0 right-0 w-full border-b border-theme-primary border-dashed bottom-2" />
                        <div className="bg-theme-slate-900 relative z-10 px-2 font-medium">
                          + Add New KPI
                        </div>
                      </button>
                    )}
                  </>
                )}
              </Form.List>
            </div>

            {/* Manager Comments */}
            {mode === "edit" && managerMappings.some((m: any) => m.kpiComment) && (
              <div className="mt-8 border border-theme-gray-700 rounded-xl p-6 space-y-4">
                <h4 className="text-white text-base font-semibold">
                  Manager Comments
                </h4>
                {managerMappings.map((mapping: any, idx: number) => {
                  if (!mapping.kpiComment) return null;
                  const name = mapping.manager
                    ? `${mapping.manager.firstName || ""} ${mapping.manager.lastName || ""}`.trim()
                    : "";
                  return (
                    <div key={mapping.id || idx}>
                      {idx > 0 && (
                        <div className="border-t border-dashed border-theme-gray-700 pt-4" />
                      )}
                      <label className="text-theme-gray-400 text-xs mb-1.5 block font-semibold tracking-wider">
                        Line Manager - {idx + 1} Comment{name ? ` (${name})` : ""}
                      </label>
                      <div className="bg-theme-slate-900 border border-theme-gray-700 rounded-lg px-4 py-3 text-white text-sm min-h-[80px] whitespace-pre-wrap">
                        {mapping.kpiComment}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Manager Comment */}
            {isTeam && (
              <div className="mt-8 border border-theme-gray-700 rounded-xl p-6">
                <Form.Item
                  label="Manager Comment"
                  name="manager_comment"
                  className="mb-0"
                >
                  <TextArea rows={4} placeholder="Enter manager comment" />
                </Form.Item>
              </div>
            )}

            {/* Attachment */}
            <Form.Item className="mt-8" name="attachments">
              <MultiFileUploader
                form={form}
                name="attachments"
                title="Drag & Drop your file here"
                description="Browse"
              />
            </Form.Item>
            <Flex justify="end" gap={12} className="mt-12">
              {isTeam && (
                <Button
                  size="large"
                  htmlType="button"
                  className="btn-primary-bordered min-w-24"
                  onClick={() => handleSubmit("return")}
                  loading={isReturningTeam}
                  disabled={isDrafting || isCreating}
                >
                  Return
                </Button>
              )}
              <Button
                size="large"
                htmlType="button"
                className="btn-primary-bordered min-w-24"
                onClick={() => handleSubmit("draft")}
                loading={isDrafting}
                disabled={isCreating || isReturningTeam}
              >
                Draft
              </Button>
              <Button
                size="large"
                htmlType="button"
                className="btn-primary min-w-24"
                onClick={() => handleSubmit("submit")}
                loading={isCreating}
                disabled={isDrafting || isReturningTeam}
              >
                Submit
              </Button>
            </Flex>
          </>
        </Form>
      </WithLoader>
    </CustomDrawer>
  );
}
